import { CityState, RoadType, ZoneType, DensityType } from './types';
import type { ProgressionTier } from './types';
import { RoadEngine } from './RoadEngine';
import { ParcelSystem } from './ParcelSystem';
import { BuildingEngine } from './BuildingEngine';
import { EconomyEngine } from './EconomyEngine';
import { DistrictEngine } from './DistrictEngine';
import { UtilityEngine } from './UtilityEngine';
import { EnvironmentEngine } from './EnvironmentEngine';
import { TrafficEngine } from './TrafficEngine';
import { SafetyEngine } from './SafetyEngine';
import { QuestEngine } from './QuestEngine';

export class SimulationEngine {
  private state: CityState;
  
  private roadEngine: RoadEngine;
  private parcelSystem: ParcelSystem;
  private buildingEngine: BuildingEngine;
  private economyEngine: EconomyEngine;
  private districtEngine: DistrictEngine;
  private utilityEngine: UtilityEngine;
  private environmentEngine: EnvironmentEngine;
  private trafficEngine: TrafficEngine;
  private safetyEngine: SafetyEngine;
  private questEngine: QuestEngine;

  private listeners: Record<string, ((...args: any[]) => void)[]> = {};
  private intervalId: any = null;

  constructor(initialState?: CityState) {
    if (initialState) {
      this.state = initialState;
    } else {
      this.state = this.createDefaultState();
    }

    this.roadEngine = new RoadEngine(this.state.roads);
    this.parcelSystem = new ParcelSystem();
    this.buildingEngine = new BuildingEngine();
    this.economyEngine = new EconomyEngine();
    this.districtEngine = new DistrictEngine();
    this.utilityEngine = new UtilityEngine();
    this.environmentEngine = new EnvironmentEngine();
    this.trafficEngine = new TrafficEngine();
    this.safetyEngine = new SafetyEngine();
    this.questEngine = new QuestEngine();

    // Initial calculations
    this.utilityEngine.tickUtilities(this.state);
    this.environmentEngine.tickEnvironment(this.state);
    this.trafficEngine.tickTraffic(this.state, this.roadEngine);
    this.safetyEngine.tickSafety(this.state);

    this.state.parcels = this.parcelSystem.updateParcels(this.state);

    if (!initialState) {
      // 1. Manually seed starting parcels for our starter city (centered in 48x48 starting sector 1,1)
      this.state.parcels['20,23'] = {
        id: 'parcel_start_res1',
        x: 20,
        y: 23,
        zone: 'residential',
        density: 'low',
        landValue: 120,
        buildingId: 'b_start_res1',
        frontageRoadKey: '20,24'
      };
      this.state.parcels['21,23'] = {
        id: 'parcel_start_res2',
        x: 21,
        y: 23,
        zone: 'residential',
        density: 'low',
        landValue: 120,
        buildingId: 'b_start_res2',
        frontageRoadKey: '21,24'
      };
      this.state.parcels['23,21'] = {
        id: 'parcel_start_com1',
        x: 23,
        y: 21,
        zone: 'commercial',
        density: 'low',
        landValue: 130,
        buildingId: 'b_start_com1',
        frontageRoadKey: '23,20'
      };
      this.state.parcels['24,21'] = {
        id: 'parcel_start_ind1',
        x: 24,
        y: 21,
        zone: 'industrial',
        density: 'low',
        landValue: 100,
        buildingId: 'b_start_ind1',
        frontageRoadKey: '24,20'
      };

      // 2. Pre-calculate auto-tiling masks for all starter roads
      for (const key of Object.keys(this.state.roads)) {
        const [rx, ry] = key.split(',').map(Number);
        let mask = 0;
        if (this.roadEngine.hasRoad(rx, ry - 1)) mask |= 1;
        if (this.roadEngine.hasRoad(rx + 1, ry)) mask |= 2;
        if (this.roadEngine.hasRoad(rx, ry + 1)) mask |= 4;
        if (this.roadEngine.hasRoad(rx - 1, ry)) mask |= 8;
        this.state.roads[key].autoTilingId = `road_tile_${mask}`;
      }

      // 3. Pre-spawn Town Hall and Solar Power Plant and Water Tower
      this.state.buildings['24,23'] = {
        id: 'b_start_townhall',
        x: 24,
        y: 23,
        zone: 'civic',
        density: 'medium',
        level: 1,
        stage: 'completed',
        progress: 100,
        occupancy: 0,
        capacity: 0,
        landValue: 200,
        name: 'Municipio Centrale',
        age: 0,
        familyId: 'townhall',
        assetVariant: 4,
        isPowered: true,
        isWatered: true
      };
      this.state.parcels['24,23'] = {
        id: 'parcel_b_start_townhall',
        x: 24,
        y: 23,
        zone: 'civic',
        density: 'medium',
        landValue: 200,
        buildingId: 'b_start_townhall',
        frontageRoadKey: null
      };

      this.state.buildings['20,21'] = {
        id: 'b_start_solar',
        x: 20,
        y: 21,
        zone: 'civic',
        density: 'medium',
        level: 1,
        stage: 'completed',
        progress: 100,
        occupancy: 0,
        capacity: 0,
        landValue: 150,
        name: 'Centrale Solare Swiss',
        age: 0,
        familyId: 'power_solar',
        assetVariant: 0,
        isPowered: true,
        isWatered: true
      };
      this.state.parcels['20,21'] = {
        id: 'parcel_b_start_solar',
        x: 20,
        y: 21,
        zone: 'civic',
        density: 'medium',
        landValue: 150,
        buildingId: 'b_start_solar',
        frontageRoadKey: null
      };

      this.state.buildings['21,21'] = {
        id: 'b_start_watertower',
        x: 21,
        y: 21,
        zone: 'civic',
        density: 'medium',
        level: 1,
        stage: 'completed',
        progress: 100,
        occupancy: 0,
        capacity: 0,
        landValue: 150,
        name: 'Torre dell\'Acqua Comunale',
        age: 0,
        familyId: 'water_tower',
        assetVariant: 1,
        isPowered: true,
        isWatered: true
      };
      this.state.parcels['21,21'] = {
        id: 'parcel_b_start_watertower',
        x: 21,
        y: 21,
        zone: 'civic',
        density: 'medium',
        landValue: 150,
        buildingId: 'b_start_watertower',
        frontageRoadKey: null
      };

      // 4. Pre-spawn central plaza park at (23, 23)
      this.state.buildings['23,23'] = {
        id: 'b_start_park',
        x: 23,
        y: 23,
        zone: 'park',
        density: 'medium',
        level: 1,
        stage: 'completed',
        progress: 100,
        occupancy: 0,
        capacity: 0,
        landValue: 180,
        name: 'Parco della Piazza',
        age: 0,
        familyId: 'park',
        assetVariant: 0,
        isPowered: true,
        isWatered: true
      };
      this.state.parcels['23,23'] = {
        id: 'parcel_b_start_park',
        x: 23,
        y: 23,
        zone: 'park',
        density: 'medium',
        landValue: 180,
        buildingId: 'b_start_park',
        frontageRoadKey: null
      };

      // 5. Pre-spawn starter R/C/I buildings on our zoned parcels
      this.state.buildings['20,23'] = {
        id: 'b_start_res1',
        x: 20,
        y: 23,
        zone: 'residential',
        density: 'low',
        level: 1,
        stage: 'completed',
        progress: 100,
        occupancy: 4,
        capacity: 4,
        landValue: 120,
        name: 'Villetta Fiorita',
        age: 0,
        familyId: 'res_suburban',
        assetVariant: 2,
        isPowered: true,
        isWatered: true
      };

      this.state.buildings['21,23'] = {
        id: 'b_start_res2',
        x: 21,
        y: 23,
        zone: 'residential',
        density: 'low',
        level: 1,
        stage: 'completed',
        progress: 100,
        occupancy: 4,
        capacity: 4,
        landValue: 120,
        name: 'Villa Sole',
        age: 0,
        familyId: 'res_modern',
        assetVariant: 3,
        isPowered: true,
        isWatered: true
      };

      this.state.buildings['23,21'] = {
        id: 'b_start_com1',
        x: 23,
        y: 21,
        zone: 'commercial',
        density: 'low',
        level: 1,
        stage: 'completed',
        progress: 100,
        occupancy: 6,
        capacity: 6,
        landValue: 130,
        name: 'Caffè della Piazza',
        age: 0,
        familyId: 'com_food',
        assetVariant: 4,
        isPowered: true,
        isWatered: true
      };

      this.state.buildings['24,21'] = {
        id: 'b_start_ind1',
        x: 24,
        y: 21,
        zone: 'industrial',
        density: 'low',
        level: 1,
        stage: 'completed',
        progress: 100,
        occupancy: 4,
        capacity: 4,
        landValue: 100,
        name: 'Officina Centrale',
        age: 0,
        familyId: 'ind_workshop',
        assetVariant: 1,
        isPowered: true,
        isWatered: true
      };

      this.state.population = 8;
      this.state.jobs = 10;

      // Recalculate utilities for the newly placed starter city buildings
      this.utilityEngine.tickUtilities(this.state);
      this.environmentEngine.tickEnvironment(this.state);
    }
  }

  public on<K extends 'tick' | 'day' | 'month' | 'year' | 'state-changed' | 'event-triggered' | 'progression-unlocked'>(
    event: K, 
    callback: (...args: any[]) => void
  ) {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(callback);
  }

  public off<K extends 'tick' | 'day' | 'month' | 'year' | 'state-changed' | 'event-triggered' | 'progression-unlocked'>(
    event: K, 
    callback: (...args: any[]) => void
  ) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
  }

  private emit(event: string, ...args: any[]) {
    const list = this.listeners[event];
    if (list) {
      for (const cb of list) cb(...args);
    }
  }

  public start() {
    if (this.intervalId) return;
    this.intervalId = setInterval(() => this.tick(), this.getTickDuration());
  }

  public stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  public setSpeed(speed: number) {
    this.state.simSpeed = speed;
    this.stop();
    if (speed > 0) this.start();
    this.emit('state-changed', this.state);
  }

  public tick() {
    if (this.state.simSpeed === 0) return;

    // 1. Recalculate utility grid transmission (Power & Water) before simulation math
    this.utilityEngine.tickUtilities(this.state);
    
    // 1.5. Recalculate pollution maps and land values before simulation math
    this.environmentEngine.tickEnvironment(this.state);

    this.advanceDate();
    this.economyEngine.tickEconomy(this.state);
    this.districtEngine.tickDistricts(this.state);
    this.buildingEngine.tickGrowth(this.state);

    // 2. Recalculate commuter traffic congestion and city safety grids (crime, fire risk)
    this.trafficEngine.tickTraffic(this.state, this.roadEngine);
    this.safetyEngine.tickSafety(this.state);

    // 3. Tick active Mayor quests
    this.questEngine.tickQuests(this.state, (msg) => this.addHistory(msg));

    // extended coverages & unlocks
    this.updateCivicCoverage();
    this.checkProgression();
    this.tryTriggerEvent();

    this.emit('tick', this.state);
  }

  public getQuests() {
    return this.questEngine.getQuests();
  }

  private advanceDate() {
    const d = this.state.date;
    d.day += 1;

    if (d.day > 30) {
      d.day = 1;
      d.month += 1;
      this.emit('month', this.state);

      if (d.month > 12) {
        d.month = 1;
        d.year += 1;
        this.emit('year', this.state);
      }
    }
    this.emit('day', this.state);
  }

  private updateCivicCoverage() {
    let schools = 0; let hospitals = 0; let safety = 0;

    for (const key of Object.keys(this.state.buildings)) {
      const b = this.state.buildings[key];
      if (b.stage === 'completed' && b.zone === 'civic') {
        if (b.name.includes('Scuola')) schools++;
        else if (b.name.includes('Ospedale')) hospitals++;
        else safety++;
      }
    }

    const popMult = this.state.population / 120 + 1;
    this.state.educationCoverage = Math.max(0, Math.min(100, Math.round((schools * 60) / popMult)));
    this.state.healthCoverage = Math.max(0, Math.min(100, Math.round((hospitals * 60) / popMult)));
    this.state.safetyCoverage = Math.max(0, Math.min(100, Math.round((safety * 50) / popMult)));

    const bonus = (this.state.educationCoverage + this.state.healthCoverage + this.state.safetyCoverage) / 10;
    this.state.happiness = Math.max(0, Math.min(100, Math.round(this.state.happiness + bonus)));
  }

  private checkProgression() {
    const pop = this.state.population;
    let newTier: ProgressionTier = this.state.progressionTier;

    if (pop < 150) {
      newTier = 'village';
      this.state.unlockedDensities = ['low'];
    } else if (pop >= 150 && pop < 600) {
      newTier = 'town';
      this.state.unlockedDensities = ['low', 'medium'];
    } else if (pop >= 600 && pop < 2000) {
      newTier = 'city';
      this.state.unlockedDensities = ['low', 'medium', 'high'];
    } else if (pop >= 2000 && pop < 5000) {
      newTier = 'large-city';
      this.state.unlockedDensities = ['low', 'medium', 'high'];
    } else {
      newTier = 'metropolis';
      this.state.unlockedDensities = ['low', 'medium', 'high'];
    }

    if (newTier !== this.state.progressionTier) {
      this.state.progressionTier = newTier;
      this.addHistory(`La città è cresciuta fino allo status di ${newTier.toUpperCase()}!`);
      this.emit('progression-unlocked', this.state, newTier);
    }
  }

  private getMonthName(m: number): string {
    const names = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return names[m - 1] || 'Mese';
  }

  public addHistory(description: string) {
    const dateStr = `${this.state.date.day} ${this.getMonthName(this.state.date.month)} ${this.state.date.year}`;
    this.state.history.unshift({
      id: `hist_${Date.now()}`,
      date: dateStr,
      description
    });
    if (this.state.history.length > 50) this.state.history.pop();
  }

  private tryTriggerEvent() {
    if (this.state.activeEvent) return;

    if (Math.random() < 0.015) {
      const eventCards = [
        {
          id: 'evt_traffic',
          title: 'Allerta Congestione Stradale',
          description: 'La crescita economica sta intasando gli assi viari principali. I cittadini chiedono a gran voce investimenti mirati!',
          choices: [
            { id: 'traffic_subsidize', label: 'Incentiva i trasporti pubblici (§400)', cost: 400, resultText: 'Strade libere! Felicità e domanda commerciale aumentate.' },
            { id: 'traffic_ignore', label: 'Ignora le lamentele (§0)', cost: 0, resultText: 'Code interminabili! La felicità cittadina cala.' }
          ]
        },
        {
          id: 'evt_housing',
          title: 'Emergenza Alloggi ed Affitti',
          description: 'L\'afflusso massiccio di lavoratori ha innescato un boom degli affitti. Gli alloggi scarseggiano e i canoni sono insostenibili.',
          choices: [
            { id: 'housing_subsidize', label: 'Sussidia edilizia popolare (§800)', cost: 800, resultText: 'Edilizia sostenuta! Felicità della popolazione alle stelle.' },
            { id: 'housing_market', label: 'Lascia fare al libero mercato (§0)', cost: 0, resultText: 'Proteste in piazza! La felicità scende.' }
          ]
        },
        {
          id: 'evt_leak',
          title: 'Disastro Ecologico Industriale',
          description: 'Una perdita tossica dagli scarichi industriali sta inquinando la costa e allarmando la cittadinanza.',
          choices: [
            { id: 'leak_fine', label: 'Multa pesantemente le fabbriche (+§300)', cost: -300, resultText: 'Aziende sanzionate! Ambiente protetto, ma cala la domanda industriale.' },
            { id: 'leak_inspectors', label: 'Insabbia la faccenda coi periti (+§800)', cost: -800, resultText: 'Tangente incassata! La felicità crolla ma rimpingui le casse.' }
          ]
        },
        {
          id: 'evt_festival',
          title: 'Sponsorizzazione Festival Elvetico',
          description: 'La pro-loco propone di ospitare il Gran Festival di Primavera a Swisslandia per attirare turisti e commercio.',
          choices: [
            { id: 'festival_sponsor', label: 'Finanzia la kermesse (§350)', cost: 350, resultText: 'Successo strepitoso! Turismo e commercio in forte aumento.' },
            { id: 'festival_decline', label: 'Rifiuta la proposta (§0)', cost: 0, resultText: 'Cittadini delusi. Nessun impatto economico.' }
          ]
        }
      ];

      const selected = eventCards[Math.floor(Math.random() * eventCards.length)];
      this.state.activeEvent = selected;
      this.setSpeed(0); // pause clock during choice popup
      this.emit('event-triggered', selected);
    }
  }

  public resolveActiveEventChoice(choiceId: string): string {
    const evt = this.state.activeEvent;
    if (!evt) return '';

    const choice = evt.choices.find(c => c.id === choiceId);
    if (!choice) return '';

    this.state.treasury -= choice.cost;
    let resultLog = '';

    if (choiceId === 'traffic_subsidize') {
      this.state.happiness = Math.min(100, this.state.happiness + 15);
      this.state.demand.commercial = Math.min(100, this.state.demand.commercial + 30);
      resultLog = 'Sponsorizzata la metropolitana leggera della città.';
    } else if (choiceId === 'traffic_ignore') {
      this.state.happiness = Math.max(0, this.state.happiness - 20);
      this.state.demand.commercial = Math.max(0, this.state.demand.commercial - 15);
      resultLog = 'Lamentele sul traffico ignorate dall\'amministrazione.';
    } else if (choiceId === 'housing_subsidize') {
      this.state.happiness = Math.min(100, this.state.happiness + 20);
      this.state.demand.residential = Math.min(100, this.state.demand.residential + 40);
      resultLog = 'Deliberato il piano di edilizia convenzionata.';
    } else if (choiceId === 'housing_market') {
      this.state.happiness = Math.max(0, this.state.happiness - 15);
      resultLog = 'Affitti liberalizzati, innescate speculazioni edilizie.';
    } else if (choiceId === 'leak_fine') {
      this.state.happiness = Math.min(100, this.state.happiness + 12);
      this.state.demand.industrial = Math.max(0, this.state.demand.industrial - 30);
      resultLog = 'Erogate pesanti sanzioni per inquinamento ambientale.';
    } else if (choiceId === 'leak_inspectors') {
      this.state.happiness = Math.max(0, this.state.happiness - 25);
      this.state.demand.industrial = Math.min(100, this.state.demand.industrial + 20);
      resultLog = 'Insabbiato lo sversamento chimico degli stabilimenti.';
    } else if (choiceId === 'festival_sponsor') {
      this.state.happiness = Math.min(100, this.state.happiness + 25);
      this.state.demand.commercial = Math.min(100, this.state.demand.commercial + 35);
      resultLog = 'Organizzato con enorme successo il Festival Elvetico.';
    } else if (choiceId === 'festival_decline') {
      this.state.happiness = Math.max(0, this.state.happiness - 5);
      resultLog = 'Respinta la proposta di sponsorizzazione culturale.';
    }

    this.addHistory(`Evento: ${evt.title} - ${resultLog}`);
    this.state.activeEvent = null;
    this.setSpeed(1); // resume normal flow
    return choice.resultText;
  }

  public triggerDisaster(type: 'tornado' | 'fire_outbreak') {
    const keys = Object.keys(this.state.buildings);
    if (keys.length === 0) return;

    if (type === 'fire_outbreak') {
      // Set 3 random completed buildings to damaged stage representing a massive fire outbreak!
      const completed = keys.filter(k => this.state.buildings[k].stage === 'completed' && this.state.buildings[k].zone !== 'park');
      const targets = completed.sort(() => Math.random() - 0.5).slice(0, 3);
      
      targets.forEach(key => {
        const b = this.state.buildings[key];
        b.stage = 'damaged';
        b.progress = 0;
        b.occupancy = 0;
      });

      this.state.happiness = Math.max(0, this.state.happiness - 15);
      this.addHistory(`🔥 DISASTRO: Scoppiato un grave incendio di massa in città! Richiesto l'intervento immediato dei pompieri!`);
      this.utilityEngine.tickUtilities(this.state);
      this.emit('state-changed', this.state);
    } 
    else if (type === 'tornado') {
      // Tornado rips across the map! Destroying 4-5 contiguous cells
      const startKey = keys[Math.floor(Math.random() * keys.length)];
      const bStart = this.state.buildings[startKey];
      
      const targets = [startKey];
      let cx = bStart.x;
      let cy = bStart.y;
      for (let i = 0; i < 4; i++) {
        cx += Math.random() > 0.5 ? 1 : -1;
        cy += Math.random() > 0.5 ? 1 : -1;
        const nkey = `${cx},${cy}`;
        if (this.state.buildings[nkey]) {
          targets.push(nkey);
        }
      }

      targets.forEach(key => {
        const b = this.state.buildings[key];
        b.stage = 'rubble'; // Tornado reduces buildings to rubble!
        b.progress = 0;
        b.occupancy = 0;
      });

      this.state.happiness = Math.max(0, this.state.happiness - 35);
      this.addHistory(`🌪️ DISASTRO: Un devastante Tornado ha colpito Swisslandia, riducendo interi isolati in macerie!`);
      this.utilityEngine.tickUtilities(this.state);
      this.emit('state-changed', this.state);
    }
  }

  public triggerEvent(type: 'festival' | 'block_party') {
    const cost = type === 'festival' ? 450 : 200;
    if (this.state.treasury < cost) return false;

    this.state.treasury -= cost;
    this.state.happiness = Math.min(100, this.state.happiness + 20);
    this.state.demand.residential = Math.min(100, this.state.demand.residential + 30);
    this.state.demand.commercial = Math.min(100, this.state.demand.commercial + 30);

    this.addHistory(`🎉 EVENTO: Finanziata con successo una splendida ${type === 'festival' ? 'Kermesse Festival' : 'Festa di Quartiere'}! Felicità aumentata!`);
    this.emit('state-changed', this.state);
    return true;
  }

  public isCellUnlocked(x: number, y: number): boolean {
    const sectorX = Math.floor(x / 16);
    const sectorY = Math.floor(y / 16);
    const key = `${sectorX},${sectorY}`;
    return !!this.state.unlockedSectors[key];
  }

  public unlockSector(sectorX: number, sectorY: number): boolean {
    const key = `${sectorX},${sectorY}`;
    if (this.state.unlockedSectors[key]) return false; // Already purchased!

    const unlockedCount = Object.keys(this.state.unlockedSectors).length;
    const cost = unlockedCount * 2500; // Progressive costs (2500, 5000, 7500...)
    
    if (this.state.treasury < cost) return false;

    this.state.treasury -= cost;
    this.state.unlockedSectors[key] = true;
    
    this.addHistory(`🗺️ ESPANSIONE: Acquistato ed unlocked il quadrante (${sectorX}, ${sectorY}) per §${cost}!`);
    this.utilityEngine.tickUtilities(this.state);
    this.environmentEngine.tickEnvironment(this.state);
    this.emit('state-changed', this.state);
    return true;
  }

  public buildRoad(x: number, y: number, type: RoadType = 'local'): boolean {
    if (!this.isCellUnlocked(x, y)) return false; // Enforce land boundary quadrant lock!
    const cost = 25;
    if (this.state.treasury < cost) return false;

    const road = this.roadEngine.addRoad(x, y, type);
    if (!road) return false;

    this.state.treasury -= cost;
    this.state.parcels = this.parcelSystem.updateParcels(this.state);
    this.utilityEngine.tickUtilities(this.state);
    this.environmentEngine.tickEnvironment(this.state);
    this.emit('state-changed', this.state);
    return true;
  }

  public demolish(x: number, y: number): boolean {
    if (!this.isCellUnlocked(x, y)) return false; // Enforce lock
    const key = `${x},${y}`;
    let success = false;
    const cost = 10;
    if (this.state.treasury < cost) return false;

    if (this.state.buildings[key]) {
      delete this.state.buildings[key];
      const p = this.state.parcels[key];
      if (p) p.buildingId = null;
      this.state.treasury -= cost;
      success = true;
    }

    if (this.roadEngine.removeRoad(x, y)) {
      this.state.treasury -= cost;
      this.state.parcels = this.parcelSystem.updateParcels(this.state);
      success = true;
    }

    if (this.state.parcels[key]) {
      delete this.state.parcels[key];
      this.state.treasury -= cost;
      success = true;
    }

    if (success) {
      this.state.parcels = this.parcelSystem.updateParcels(this.state);
      this.utilityEngine.tickUtilities(this.state);
      this.environmentEngine.tickEnvironment(this.state);
      this.emit('state-changed', this.state);
    }
    return success;
  }

  public paintZone(x: number, y: number, zoneType: ZoneType, density: DensityType = 'low'): boolean {
    if (!this.isCellUnlocked(x, y)) return false; // Enforce lock
    const cost = 10;
    if (this.state.treasury < cost) return false;

    const key = `${x},${y}`;

    // Can only paint zones on empty terrain, not on top of existing roads, buildings, or other zones
    if (this.state.roads[key] || this.state.buildings[key] || this.state.parcels[key]) return false;

    // Check buildable terrain type: 1 (sand), 2 (grass)
    const terrainType = this.state.terrain[y]?.[x];
    if (terrainType !== 1 && terrainType !== 2) return false;

    this.state.treasury -= cost;
    this.state.parcels[key] = {
      id: `parcel_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      x,
      y,
      zone: zoneType,
      density,
      landValue: 100,
      buildingId: null,
      frontageRoadKey: null
    };

    this.state.parcels = this.parcelSystem.updateParcels(this.state);
    this.utilityEngine.tickUtilities(this.state);
    this.environmentEngine.tickEnvironment(this.state);
    this.emit('state-changed', this.state);
    return true;
  }

  public buildCivic(x: number, y: number, type: string): boolean {
    if (!this.isCellUnlocked(x, y)) return false; // Enforce lock
    const costs: Record<string, number> = {
      park: 150,
      power_solar: 450,
      power_coal: 800,
      water_tower: 300,
      water_pump: 550,
      police: 400,
      fire: 400,
      hospital: 600,
      school: 500,
      port: 800,
      airport: 1400,
      central_station: 1800,
      panoramic_tower: 3000,
      stadium: 5000
    };
    const cost = costs[type] || 300;
    if (this.state.treasury < cost) return false;

    // spawn the custom civic building
    const b = this.buildingEngine.spawnCivicBuilding(x, y, this.state, type);
    if (!b) return false;

    // customize assetVariant for genetic rendering fallbacks
    if (type === 'police') b.assetVariant = 2;
    else if (type === 'fire') b.assetVariant = 1;
    else if (type === 'hospital') b.assetVariant = 3;
    else if (type === 'school') b.assetVariant = 0;
    else if (type === 'park') b.assetVariant = Math.floor(Math.random() * 5);
    else if (type === 'port') b.assetVariant = 0;
    else if (type === 'airport') b.assetVariant = 1;
    else if (type === 'central_station') b.assetVariant = 2;
    else if (type === 'panoramic_tower') b.assetVariant = 3;
    else if (type === 'stadium') b.assetVariant = 4;

    this.state.treasury -= cost;
    this.districtEngine.tickDistricts(this.state);
    this.utilityEngine.tickUtilities(this.state);
    this.environmentEngine.tickEnvironment(this.state);
    this.emit('state-changed', this.state);
    return true;
  }

  public setTaxRate(rate: number) {
    this.state.taxRate = Math.max(0.01, Math.min(0.30, rate));
    this.emit('state-changed', this.state);
  }

  private getTickDuration(): number {
    const base = 1000;
    if (this.state.simSpeed === 1) return base;
    if (this.state.simSpeed === 2) return base / 2;
    if (this.state.simSpeed === 5) return base / 5;
    return base;
  }

  public saveCity(): string {
    return JSON.stringify(this.state);
  }

  public loadCity(serializedState: string) {
    try {
      const loaded = JSON.parse(serializedState) as CityState;
      this.state = loaded;
      this.roadEngine = new RoadEngine(this.state.roads);
      this.emit('state-changed', this.state);
    } catch (e) {
      console.error('Failed to load city state:', e);
    }
  }

  public getState(): CityState {
    return this.state;
  }

  public getRoadEngine(): RoadEngine {
    return this.roadEngine;
  }

  private createDefaultState(): CityState {
    const size = 48; // Expanded map size for dynamic land quadrants unlocking!
    const terrain: number[][] = [];

    // Seeded random island generation for size 48x48
    for (let y = 0; y < size; y++) {
      const row: number[] = [];
      for (let x = 0; x < size; x++) {
        const dx = x - size / 2 + 0.5;
        const dy = y - size / 2 + 0.5;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const normalizedDist = dist / (size / 2);

        // Procedural sinusoidal noise
        const noise = Math.sin(x * 0.4) * Math.cos(y * 0.4) * 0.12 + 
                      Math.sin(x * 0.1) * Math.sin(y * 0.1) * 0.08;

        const val = normalizedDist + noise;

        let tile = 2; // Default: grass
        if (val > 0.88) {
          tile = 0; // Coastal Water
        } else if (val > 0.77) {
          tile = 1; // Sandy beach
        } else {
          // Rocky mountain peaks in the outer sectors
          const mountainNoise = Math.cos(x * 0.5) * Math.sin(y * 0.5);
          if (mountainNoise > 0.74 && dist > 10.0) {
            tile = 3; // Rock peaks
          }
        }
        row.push(tile);
      }
      terrain.push(row);
    }

    // Pre-populate road loop grid (centered in the starting quadrant (1, 1), coordinates 20..26)
    const roads: Record<string, any> = {};
    
    // Rows
    for (const y of [20, 22, 24]) {
      for (let x = 19; x <= 25; x++) {
        roads[`${x},${y}`] = {
          id: `road_start_${x}_${y}`,
          x,
          y,
          type: 'local',
          autoTilingId: 'road_tile_0'
        };
      }
    }

    // Columns
    for (const x of [19, 22, 25]) {
      for (let y = 20; y <= 24; y++) {
        roads[`${x},${y}`] = {
          id: `road_start_${x}_${y}`,
          x,
          y,
          type: 'local',
          autoTilingId: 'road_tile_0'
        };
      }
    }

    return {
      name: 'Swisslandia 3D',
      width: size,
      height: size,
      terrain,
      buildings: {},
      roads,
      parcels: {},
      treasury: 13500, // Balanced starter funds for purchasing land
      population: 0,
      jobs: 0,
      demand: {
        residential: 65,
        commercial: 40,
        industrial: 30
      },
      taxRate: 0.10, // legacy fallback
      taxRates: {
        residential: 0.10,
        commercial: 0.10,
        industrial: 0.10
      },
      date: { year: 2026, month: 9, day: 14 },
      happiness: 85,
      simSpeed: 1,
      progressionTier: 'village',
      history: [{ id: 'found', date: '14 Set 2026', description: 'Fondata la gloriosa città di Swisslandia 3D!' }],
      activeEvent: null,
      educationCoverage: 0,
      healthCoverage: 0,
      safetyCoverage: 0,
      unlockedDensities: ['low'],

      // Mayor Level & XP Progression
      cityLevel: 1,
      xp: 0,
      xpNeeded: 100,
      sandboxMode: false,

      // Simulation grids (heatmaps)
      powerGrid: {},
      waterGrid: {},
      pollutionGrid: {},
      crimeGrid: {},
      fireGrid: {},
      trafficGrid: {},
      landValueGrid: {},

      // Dynamic sector grid unlocks. Starting center quadrant (1, 1) unlocked by default.
      // 0 = x: 0..15, 1 = x: 16..31, 2 = x: 32..47
      unlockedSectors: { '1,1': true }
    };
  }
}
